import React from 'react';
import PropTypes from 'prop-types';
import MarkdownRenderer from './MarkdownRenderer';

/**
 * ChatBubble - Reusable chat message bubble component
 *
 * Provides sleek, modern chat bubbles with:
 * - Asymmetric border radius (speech tail effect)
 * - Subtle shadows for depth
 * - Responsive sizing
 * - User messages on right (blue), AI messages on left (gray)
 *
 * Used by: AI Coach, Report Writer
 */
const ChatBubble = ({
  role,
  content,
  children,
  isMobile = false,
  className = ''
}) => {
  const isUser = role === 'user';
  const isAI = role === 'ai' || role === 'assistant';

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        width: '100%',
        marginBottom: '12px'
      }}
      className={className}
    >
      <div
        style={{
          maxWidth: isMobile ? '85%' : '80%',
          backgroundColor: isUser ? 'var(--rlc-state-info)' : 'var(--rlc-surface-sunken)',
          color: isUser ? 'white' : 'var(--rlc-neutral-900)',
          padding: isMobile ? '10px 14px' : '12px 16px',
          borderRadius: isUser
            ? '18px 18px 4px 18px'  // User: sharp bottom-right corner (speech tail)
            : '18px 18px 18px 4px', // AI: sharp bottom-left corner (speech tail)
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          boxShadow: isUser
            ? '0 2px 8px rgba(6, 62, 68, 0.15)'  // Subtle blue shadow for user
            : '0 2px 8px rgba(0, 0, 0, 0.08)',       // Subtle gray shadow for AI
          position: 'relative'
        }}
        className={`chat-bubble ${isUser ? 'user-bubble' : 'ai-bubble'}`}
      >
        {isAI ? (
          <div className="assistant-message">
            <MarkdownRenderer content={content} />
            {children}
          </div>
        ) : (
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {content}
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

ChatBubble.propTypes = {
  role: PropTypes.oneOf(['user', 'ai', 'assistant']).isRequired,
  content: PropTypes.string.isRequired,
  children: PropTypes.node,
  isMobile: PropTypes.bool,
  className: PropTypes.string,
};

export default ChatBubble;
